// components/ReferralsPage.tsx — account-area referrals dashboard.
//
// Modal-ish overlay (same shell pattern as Bookshelf). Loads
// GET /api/referrals/dashboard once on mount; surfaces:
//
//   - Lifetime turns earned (hero stat)
//   - "Frozen" banner if the user's own subscription is cancelled
//   - Active referees as first-name chips
//   - Pending count
//   - Tier progress bars (visualise the diminishing-tier ladder)
//   - Per-book breakdown table
//
// Components read from props (no Redux on the player surface — the data
// arrives via fetch). Loading + error states keep the surface usable
// even when the dashboard fetch fails.

import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  getReferralDashboard,
  type ReferralDashboard,
} from "../services/referralsApi";
import { shareStory } from "../services/booksApi";
import type { StoryListEntry } from "../services/storiesApi";

export interface ReferralsPageProps {
  onClose: () => void;
  /** All of the user's stories — used to populate the share-a-book picker. */
  allStories: StoryListEntry[];
}

const SHARE_BASE =
  (import.meta.env.VITE_STORY_WEB_ORIGIN as string | undefined) ??
  "https://story.emberhollow.ai";

export default function ReferralsPage({
  onClose,
  allStories,
}: ReferralsPageProps) {
  const { t } = useTranslation(["referrals", "common"]);
  const [data, setData] = useState<ReferralDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getReferralDashboard();
        if (cancelled) return;
        setData(res);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Escape closes the overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const close = useCallback(() => onClose(), [onClose]);

  return (
    <div style={backdrop} role="dialog" aria-modal="true">
      <div style={card}>
        <header style={cardHeader}>
          <h2 style={titleStyle}>{t("referrals:page.title")}</h2>
          <button
            type="button"
            style={closeBtn}
            onClick={close}
            aria-label={t("common:buttons.close")}
          >
            ×
          </button>
        </header>

        {loading ? (
          <div style={emptyState}>{t("referrals:page.loading")}</div>
        ) : error ? (
          <div style={{ ...emptyState, color: "#e08585" }}>
            {t("referrals:page.loadError", { message: error })}
          </div>
        ) : data ? (
          <DashboardBody data={data} />
        ) : null}

        <footer style={cardFooter}>
          <button
            type="button"
            style={shareCtaBtn}
            onClick={() => setPickerOpen(true)}
          >
            {t("referrals:page.shareCta")}
          </button>
        </footer>
      </div>

      {pickerOpen && (
        <SharePicker
          stories={allStories}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * Stacked-modal picker shown when the user clicks "Share any of your
 * published books". Lists every story; clicking one publishes (idempotent)
 * via shareStory and copies the public URL to the clipboard.
 */
function SharePicker({
  stories,
  onClose,
}: {
  stories: StoryListEntry[];
  onClose: () => void;
}) {
  const { t } = useTranslation(["referrals", "common"]);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handlePick(story: StoryListEntry) {
    if (pendingId) return;
    setPendingId(story.id);
    setErrorId(null);
    try {
      const res = await shareStory(story.id);
      const url = res.url || `${SHARE_BASE}/s/${res.slug}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(story.id);
      window.setTimeout(() => setCopiedId(null), 1800);
    } catch {
      setErrorId(story.id);
      window.setTimeout(() => setErrorId(null), 2400);
    } finally {
      setPendingId(null);
    }
  }

  return (
    <div
      style={pickerBackdrop}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div style={pickerCard} onClick={(e) => e.stopPropagation()}>
        <header style={cardHeader}>
          <h3 style={pickerTitle}>{t("referrals:picker.title")}</h3>
          <button
            type="button"
            style={closeBtn}
            onClick={onClose}
            aria-label={t("common:buttons.close")}
          >
            ×
          </button>
        </header>
        <div style={pickerBody}>
          <p style={pickerHint}>{t("referrals:picker.hint")}</p>
          {stories.length === 0 ? (
            <div style={emptyHint}>{t("referrals:picker.empty")}</div>
          ) : (
            <ul style={pickerList}>
              {stories.map((s) => {
                const isPending = pendingId === s.id;
                const isCopied = copiedId === s.id;
                const isError = errorId === s.id;
                const label = isCopied
                  ? t("referrals:picker.copied")
                  : isError
                    ? t("referrals:picker.copyFailed")
                    : isPending
                      ? t("referrals:picker.copying")
                      : t("referrals:picker.copyLink");
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      style={pickerItem}
                      onClick={() => handlePick(s)}
                      disabled={isPending}
                    >
                      <span style={pickerItemName}>
                        {s.name || t("referrals:page.untitled")}
                      </span>
                      <span
                        style={{
                          ...pickerItemAction,
                          color: isCopied
                            ? "#7fc086"
                            : isError
                              ? "#e08585"
                              : "var(--accent, #d4a574)",
                        }}
                      >
                        {label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function DashboardBody({ data }: { data: ReferralDashboard }) {
  const { t } = useTranslation("referrals");
  return (
    <div style={body}>
      {data.frozen && (
        <div style={frozenBanner}>{t("page.frozenBanner")}</div>
      )}

      <section style={heroSection}>
        <div style={heroLabel}>{t("page.heroLabel")}</div>
        <div style={heroNumber}>
          {new Intl.NumberFormat("en-US").format(data.totalTurnsEarnedLifetime)}
        </div>
        <div style={heroSub}>
          {renderRateLine(t, data.currentMonthlyBonusRate)}
        </div>
      </section>

      <section style={twoColRow}>
        <div style={col}>
          <h3 style={sectionTitle}>{t("page.activeReferees")}</h3>
          {data.activeReferees.length === 0 ? (
            <div style={emptyHint}>{t("page.noActive")}</div>
          ) : (
            <div style={chipRow}>
              {data.activeReferees.map((r) => (
                <span
                  key={`${r.firstName}-${r.attributedAt}`}
                  style={chip}
                  title={t("page.joinedTitle", { date: formatDate(r.attributedAt) })}
                >
                  {r.firstName}
                </span>
              ))}
            </div>
          )}
          {data.pendingReferees > 0 && (
            <div style={pendingHint}>
              {t("page.pending", { count: data.pendingReferees })}
            </div>
          )}
          {data.flaggedCount > 0 && (
            <div style={flaggedHint}>
              {t("page.flagged", { count: data.flaggedCount })}
            </div>
          )}
        </div>

        <div style={col}>
          <h3 style={sectionTitle}>{t("page.tierProgress")}</h3>
          <TierBar
            label={t("page.tier1Label")}
            used={data.tierProgress.tier1.used}
            max={data.tierProgress.tier1.max}
            active={data.tierProgress.tier1.used > 0}
          />
          <TierBar
            label={t("page.tier2Label")}
            used={data.tierProgress.tier2.used}
            max={data.tierProgress.tier2.max}
            active={data.tierProgress.tier2.used > 0}
          />
          <TierBar
            label={t("page.tier3Label")}
            used={data.tierProgress.tier3.used}
            max={data.tierProgress.tier3.max}
            active={data.tierProgress.tier3.used > 0}
          />
          <TierBar
            label={t("page.tier4Label")}
            used={data.tierProgress.tier4.used}
            max={null}
            active={data.tierProgress.tier4.used > 0}
          />
          <div style={capRow}>
            <div style={capLabel}>{t("page.monthlyCap")}</div>
            <div style={capBar}>
              <div
                style={{
                  ...capFill,
                  width: `${Math.min(100, Math.round(data.tierProgress.capProgress * 100))}%`,
                }}
              />
            </div>
            <div style={capValue}>
              {data.currentMonthlyBonusRate} / 1500
            </div>
          </div>
        </div>
      </section>

      {data.byBook.length > 0 && (
        <section>
          <h3 style={sectionTitle}>{t("page.byBook")}</h3>
          <table style={byBookTable}>
            <thead>
              <tr>
                <th style={byBookTh}>{t("page.byBookStory")}</th>
                <th style={{ ...byBookTh, textAlign: "right" }}>
                  {t("page.byBookSignups")}
                </th>
                <th style={{ ...byBookTh, textAlign: "right" }}>
                  {t("page.byBookSubscribed")}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.byBook.map((b) => (
                <tr key={b.slug}>
                  <td style={byBookTd}>
                    <a
                      href={`${SHARE_BASE}/s/${b.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      style={byBookLink}
                    >
                      {b.title || t("page.untitled")}
                    </a>
                  </td>
                  <td style={{ ...byBookTd, textAlign: "right" }}>{b.signups}</td>
                  <td style={{ ...byBookTd, textAlign: "right" }}>
                    {b.subscribed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

/**
 * Render the "current monthly bonus rate: <rate>...</rate>" line with the
 * rate inlined in a <strong>. Split on the `<rate>` tag the translation
 * key carries.
 */
function renderRateLine(
  t: (key: string, opts?: Record<string, unknown>) => string,
  rate: number,
): JSX.Element {
  const raw = t("page.currentRate", { rate: `${rate} turns/mo` });
  const open = "<rate>";
  const close = "</rate>";
  const a = raw.indexOf(open);
  const b = a >= 0 ? raw.indexOf(close, a + open.length) : -1;
  if (a < 0 || b < 0) return <>{raw}</>;
  const before = raw.slice(0, a);
  const inside = raw.slice(a + open.length, b);
  const after = raw.slice(b + close.length);
  return (
    <>
      {before}
      <strong style={{ color: "var(--accent, #d4a574)" }}>{inside}</strong>
      {after}
    </>
  );
}

function TierBar({
  label,
  used,
  max,
  active,
}: {
  label: string;
  used: number;
  max: number | null;
  active: boolean;
}) {
  const pct = max && max > 0 ? Math.min(100, Math.round((used / max) * 100)) : used > 0 ? 100 : 0;
  return (
    <div style={tierBarWrap}>
      <div style={tierBarLabelRow}>
        <span style={{ ...tierBarLabel, color: active ? "var(--accent, #d4a574)" : "var(--ink-muted, #8a7355)" }}>
          {label}
        </span>
        <span style={tierBarUsed}>
          {used}
          {max != null ? `/${max}` : ""}
        </span>
      </div>
      <div style={tierBarTrack}>
        <div
          style={{
            ...tierBarFill,
            width: `${pct}%`,
            opacity: active ? 1 : 0.35,
          }}
        />
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// ── Styles ──────────────────────────────────────────────────────────────

const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 6, 4, 0.78)",
  zIndex: 200,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "48px 16px",
  overflowY: "auto",
};

const card: CSSProperties = {
  // Opaque dark background — scenario themes set --paper to translucent
  // values (e.g. rgba(20,14,10,0.55)) which let the page underneath bleed
  // through and made the modal hard to read. Use a solid colour instead.
  background: "#15100b",
  color: "var(--ink, #e8d9b8)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  maxWidth: 880,
  width: "100%",
  padding: 0,
  boxShadow: "0 28px 70px rgba(0,0,0,0.7)",
  fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
  display: "flex",
  flexDirection: "column",
};

const cardHeader: CSSProperties = {
  padding: "20px 28px 14px",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: "1.5rem",
  fontWeight: 500,
  color: "var(--accent, #d4a574)",
  letterSpacing: "0.02em",
};

const closeBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  color: "var(--ink-muted, #8a7355)",
  fontSize: "1.4rem",
  width: 32,
  height: 32,
  borderRadius: 2,
  cursor: "pointer",
  fontFamily: "inherit",
  lineHeight: 1,
};

const body: CSSProperties = {
  padding: "20px 28px 8px",
  display: "flex",
  flexDirection: "column",
  gap: 22,
};

const cardFooter: CSSProperties = {
  borderTop: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  padding: "16px 28px 20px",
  display: "flex",
  justifyContent: "center",
};

const shareCtaBtn: CSSProperties = {
  background: "transparent",
  border: "1px solid var(--accent, #d4a574)",
  color: "var(--accent, #d4a574)",
  padding: "10px 22px",
  fontSize: "0.95rem",
  fontFamily: "inherit",
  fontStyle: "italic",
  letterSpacing: "0.04em",
  cursor: "pointer",
  borderRadius: 2,
};

const pickerBackdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 6, 4, 0.78)",
  zIndex: 220,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: "80px 16px",
  overflowY: "auto",
};

const pickerCard: CSSProperties = {
  background: "#15100b",
  color: "var(--ink, #e8d9b8)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  maxWidth: 520,
  width: "100%",
  boxShadow: "0 28px 70px rgba(0,0,0,0.7)",
  fontFamily: "var(--font-body, 'Cormorant Garamond', Georgia, serif)",
  display: "flex",
  flexDirection: "column",
};

const pickerTitle: CSSProperties = {
  margin: 0,
  fontSize: "1.2rem",
  fontWeight: 500,
  color: "var(--accent, #d4a574)",
  letterSpacing: "0.02em",
};

const pickerBody: CSSProperties = {
  padding: "16px 22px 22px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const pickerHint: CSSProperties = {
  margin: 0,
  fontSize: "0.85rem",
  color: "var(--ink-muted, #8a7355)",
  fontStyle: "italic",
  lineHeight: 1.5,
};

const pickerList: CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const pickerItem: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "10px 14px",
  background: "rgba(212,165,116,0.04)",
  border: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  borderRadius: 2,
  cursor: "pointer",
  color: "var(--ink, #e8d9b8)",
  fontFamily: "inherit",
  fontSize: "0.95rem",
  textAlign: "left",
};

const pickerItemName: CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const pickerItemAction: CSSProperties = {
  fontSize: "0.78rem",
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  flexShrink: 0,
};

const emptyState: CSSProperties = {
  padding: 48,
  textAlign: "center",
  color: "var(--ink-muted, #8a7355)",
  fontStyle: "italic",
};

const heroSection: CSSProperties = {
  padding: "18px 22px",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  borderRadius: 2,
  textAlign: "center",
  background: "rgba(212,165,116,0.04)",
};

const heroLabel: CSSProperties = {
  fontSize: "0.7rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--ink-muted, #8a7355)",
  fontFamily: "'JetBrains Mono', monospace",
};

const heroNumber: CSSProperties = {
  fontSize: "3rem",
  fontWeight: 500,
  color: "var(--accent, #d4a574)",
  margin: "6px 0 4px",
  fontFamily: "'IM Fell English', Georgia, serif",
};

const heroSub: CSSProperties = {
  fontSize: "0.95rem",
  color: "var(--ink, #e8d9b8)",
};

const twoColRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 22,
};

const col: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minWidth: 0,
};

const sectionTitle: CSSProperties = {
  margin: "0 0 8px",
  fontSize: "0.75rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "var(--ink-muted, #8a7355)",
  fontFamily: "'JetBrains Mono', monospace",
};

const chipRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 6,
};

const chip: CSSProperties = {
  padding: "4px 10px",
  background: "rgba(212,165,116,0.12)",
  border: "1px solid var(--accent-muted, rgba(212,165,116,0.45))",
  color: "var(--accent, #d4a574)",
  fontSize: "0.85rem",
  borderRadius: 12,
  fontStyle: "italic",
};

const emptyHint: CSSProperties = {
  fontStyle: "italic",
  fontSize: "0.85rem",
  color: "var(--ink-muted, #8a7355)",
};

const pendingHint: CSSProperties = {
  marginTop: 6,
  fontSize: "0.8rem",
  fontStyle: "italic",
  color: "var(--ink-muted, #8a7355)",
};

const flaggedHint: CSSProperties = {
  marginTop: 4,
  fontSize: "0.78rem",
  color: "#e8c180",
  fontStyle: "italic",
};

const frozenBanner: CSSProperties = {
  padding: "10px 14px",
  background: "rgba(232,193,128,0.08)",
  border: "1px solid rgba(232,193,128,0.4)",
  color: "#e8c180",
  fontSize: "0.88rem",
  borderRadius: 2,
  lineHeight: 1.5,
};

const tierBarWrap: CSSProperties = {
  marginBottom: 8,
};

const tierBarLabelRow: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 4,
};

const tierBarLabel: CSSProperties = {
  fontSize: "0.78rem",
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.04em",
};

const tierBarUsed: CSSProperties = {
  fontSize: "0.78rem",
  fontFamily: "'JetBrains Mono', monospace",
  color: "var(--ink, #e8d9b8)",
};

const tierBarTrack: CSSProperties = {
  height: 6,
  background: "rgba(212,165,116,0.1)",
  borderRadius: 3,
  overflow: "hidden",
};

const tierBarFill: CSSProperties = {
  height: "100%",
  background: "var(--accent, #d4a574)",
  transition: "width 0.4s ease-out",
};

const capRow: CSSProperties = {
  marginTop: 12,
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const capLabel: CSSProperties = {
  fontSize: "0.74rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-muted, #8a7355)",
  fontFamily: "'JetBrains Mono', monospace",
  minWidth: 110,
};

const capBar: CSSProperties = {
  flex: 1,
  height: 6,
  background: "rgba(212,165,116,0.1)",
  borderRadius: 3,
  overflow: "hidden",
};

const capFill: CSSProperties = {
  height: "100%",
  background: "linear-gradient(to right, #d4a574, #e8c180)",
  transition: "width 0.4s ease-out",
};

const capValue: CSSProperties = {
  fontSize: "0.78rem",
  fontFamily: "'JetBrains Mono', monospace",
  color: "var(--ink, #e8d9b8)",
  minWidth: 70,
  textAlign: "right",
};

const byBookTable: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  marginTop: 6,
};

const byBookTh: CSSProperties = {
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid var(--paper-edge, rgba(212,165,116,0.18))",
  fontSize: "0.72rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  fontFamily: "'JetBrains Mono', monospace",
  color: "var(--ink-muted, #8a7355)",
  fontWeight: 500,
};

const byBookTd: CSSProperties = {
  padding: "10px 10px",
  borderBottom: "1px dashed var(--paper-edge, rgba(212,165,116,0.18))",
  fontSize: "0.92rem",
  color: "var(--ink, #e8d9b8)",
};

const byBookLink: CSSProperties = {
  color: "var(--accent, #d4a574)",
  textDecoration: "none",
  fontStyle: "italic",
};
